import { type SystemI18n } from '@myre/domain-i18n-wordings';
import { loggedUserSelector, WORDINGS } from '@myre/ui-domain-core';
import { DynamicEnvService } from '@myre/ui-domain-http';
import { LANGLOCALE, UnsubscribeOnDestroy } from '@myre/ui-primitive-core';
import { ModalService } from '@myre/ui-primitive-modal';

import { Directive, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { CookieService, type CookieOptions } from 'ngx-cookie-service';
import { type Subscription } from 'rxjs';

import { type RootState } from '../../core/core.module';
import { AuthService } from '../../guards/auth.service';

export const COOKIE_PREFIX = 'myre-lang';
export const cookieOptions: CookieOptions = {
  path: '/',
  secure: false,
};

@Directive()
export abstract class InAppNavbarComponent {
  public selectedLang = inject(LANGLOCALE);
  protected readonly _authService = inject(AuthService);
  protected readonly _cookieService = inject(CookieService);
  protected readonly _dynamicEnvService = inject(DynamicEnvService);
  protected readonly _modalService = inject(ModalService);
  protected readonly _router = inject(Router);
  protected readonly _store$ = inject(Store<RootState>);
  protected readonly _titleService = inject(Title);
  protected readonly _activatedRoute = inject(ActivatedRoute);
  protected readonly _wordings = inject<SystemI18n>(WORDINGS);

  public loggedUser$ = this._store$.select(loggedUserSelector);

  @UnsubscribeOnDestroy()
  protected _subscriptions: Subscription[] = [];

  public changeLang(lang: string, confirmation: boolean = true): void {
    lang = lang.toLowerCase();

    if (this.selectedLang.toLowerCase() !== lang) {
      if (!confirmation) {
        return this._changeLang(lang);
      }

      this._modalService.generateYesNoModal({
        text: this._wordings._system.changeLang.warning(),
        onSubmitFct: () => this._changeLang(lang),
        type: 'info',
        closeOnSubmit: false,
      });
    }
  }

  public get baseUrl(): string {
    return '/';
  }

  /**
   * TODO #UI-1 Put this into the UI repository, alongside the one in SettingsComponent
   */
  protected _changeLang(lang: string): void {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);

    const options = Object.assign({}, cookieOptions);
    options.expires = expires;
    options.domain = this._dynamicEnvService.env.domain;
    options.secure = false;
    options.path = '/';

    this._cookieService.set(
      COOKIE_PREFIX + this._dynamicEnvService.env.cookieSuffix,
      lang,
      options,
    );

    location.reload();
  }
}
